import type { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { storage } from '../storage';
import { isNetworkError } from './errors';
import { RETRY_CONFIG } from './config';

/**
 * Enhanced request interceptor with retry support
 */
export function createRequestInterceptor() {
  return {
    onFulfilled: (config: InternalAxiosRequestConfig) => {
      // Get token from storage and add to headers
      const token = storage.getToken();
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Add retry configuration to request metadata
      if (!config.metadata) {
        config.metadata = {};
      }
      config.metadata.retryCount = 0;

      return config;
    },
    onRejected: (error: AxiosError) => {
      console.error('Request interceptor error:', error);
      return Promise.reject(error);
    },
  };
}

/**
 * Enhanced response interceptor with global error handling
 */
export function createResponseInterceptor() {
  return {
    onFulfilled: (response: AxiosResponse) => {
      return response;
    },
    onRejected: async (error: AxiosError) => {
      // Handle different types of errors
      if (isNetworkError(error)) {
        return handleNetworkError(error);
      }

      if (error.response?.status === 401) {
        return handleAuthenticationError(error);
      }

      if (error.response?.status === 403) {
        return handleAuthorizationError(error);
      }

      if (error.response?.status === 429) {
        return handleRateLimitError(error);
      }

      if (error.response && error.response.status >= 500) {
        return handleServerError(error);
      }

      if (error.response?.status === 422) {
        return handleValidationError(error);
      }

      // Handle other client errors (4xx) - just log them, don't show notifications
      if (error.response && error.response.status >= 400 && error.response.status < 500) {
        console.warn('Client error:', error);
      }

      return Promise.reject(error);
    },
  };
}

/**
 * Handle network connectivity errors
 */
async function handleNetworkError(error: AxiosError): Promise<never> {
  console.error('Network error:', error);

  // Check if we should retry the request
  const config = error.config as InternalAxiosRequestConfig & {
    metadata?: { retryCount?: number };
    _retry?: boolean;
  };

  if (config && !config._retry && (config.metadata?.retryCount || 0) < RETRY_CONFIG.MAX_RETRIES) {
    config._retry = true;
    config.metadata = config.metadata || {};
    config.metadata.retryCount = (config.metadata.retryCount || 0) + 1;

    const delay = Math.min(
      RETRY_CONFIG.INITIAL_DELAY * Math.pow(2, config.metadata.retryCount - 1),
      RETRY_CONFIG.MAX_DELAY
    );

    console.log(
      `Retrying request in ${delay}ms... (Attempt ${config.metadata.retryCount}/${RETRY_CONFIG.MAX_RETRIES})`
    );

    // Wait and retry
    await new Promise(resolve => setTimeout(resolve, delay));

    try {
      const axios = (await import('./client')).default;
      return await axios.request(config);
    } catch (retryError) {
      if (config.metadata.retryCount >= RETRY_CONFIG.MAX_RETRIES) {
        console.error('Max retries exceeded for network error');
      }
      throw retryError;
    }
  }

  console.error('Network request failed');
  return Promise.reject(error);
}

/**
 * Handle authentication errors (401) with automatic token refresh.
 * Deduplicates concurrent refresh attempts via isRefreshing + a queue of
 * pending requests, resolved together once the in-flight refresh completes.
 */
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
  config: InternalAxiosRequestConfig;
}> = [];

function processQueue(success: boolean) {
  failedQueue.forEach(({ resolve, reject, config }) => {
    if (success) {
      // Update the Authorization header with the new token
      const newToken = storage.getToken();
      if (newToken) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${newToken}`;
      }
      import('./client').then(mod => {
        mod.default.request(config).then(resolve).catch(reject);
      });
    } else {
      reject(new Error('Token refresh failed'));
    }
  });
  failedQueue = [];
}

async function handleAuthenticationError(error: AxiosError): Promise<unknown> {
  const originalConfig = error.config as InternalAxiosRequestConfig;

  // Don't retry auth endpoints or already-retried requests
  if (!originalConfig || originalConfig.url?.includes('/v1/auth/') || originalConfig._retry) {
    return Promise.reject(error);
  }

  if (isRefreshing) {
    // Another request is already refreshing — queue this one
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject, config: originalConfig });
    });
  }

  originalConfig._retry = true;
  isRefreshing = true;

  try {
    const refreshToken = storage.getRefreshToken();
    if (!refreshToken) {
      processQueue(false);
      return Promise.reject(error);
    }

    // Call refresh endpoint with a raw axios instance (no interceptors) to avoid loops
    const rawAxios = (await import('axios')).default;
    const { API_BASE_URL, API_ENDPOINTS } = await import('./config');
    const refreshResponse = await rawAxios.post(
      `${API_BASE_URL}${API_ENDPOINTS.AUTH.REFRESH}`,
      { refresh_token: refreshToken },
      { headers: { 'Content-Type': 'application/json' } },
    );
    const response = refreshResponse.data;

    storage.setToken(response.access_token);
    if (response.refresh_token) {
      storage.setRefreshToken(response.refresh_token);
    }

    processQueue(true);

    // Retry the original request with new token
    originalConfig.headers = originalConfig.headers || {};
    originalConfig.headers.Authorization = `Bearer ${response.access_token}`;
    const axios = (await import('./client')).default;
    return axios.request(originalConfig);
  } catch (refreshError) {
    processQueue(false);
    // Refresh failed — clear auth state, user will be redirected to login
    storage.clearAuthData();
    return Promise.reject(refreshError);
  } finally {
    isRefreshing = false;
  }
}

/**
 * Handle authorization errors (403)
 */
function handleAuthorizationError(error: AxiosError): Promise<never> {
  console.error('Authorization error:', error);
  return Promise.reject(error);
}

/**
 * Handle rate limiting errors (429)
 */
function handleRateLimitError(error: AxiosError): Promise<never> {
  console.error('Rate limit error:', error);

  // Extract retry-after header if available
  const retryAfter = error.response?.headers['retry-after'];
  const waitTime = retryAfter ? parseInt(retryAfter) : 60;

  console.warn(`Rate limited. Wait ${waitTime} seconds before retrying.`);
  return Promise.reject(error);
}

/**
 * Handle server errors (5xx)
 */
function handleServerError(error: AxiosError): Promise<never> {
  console.error('Server error:', error);
  return Promise.reject(error);
}

/**
 * Handle validation errors (422)
 */
function handleValidationError(error: AxiosError): Promise<never> {
  // Don't show global notification for validation errors
  // These should be handled by individual forms
  console.warn('Validation error:', error);
  return Promise.reject(error);
}

/**
 * Enhanced error logging for debugging
 */
export function logError(error: AxiosError, context?: string) {
  const errorData = {
    message: error.message,
    status: error.response?.status,
    statusText: error.response?.statusText,
    url: error.config?.url,
    method: error.config?.method,
    timestamp: new Date().toISOString(),
    context,
  };

  if (import.meta.env.DEV) {
    console.group('🔴 API Error Details');
    console.error('Error:', errorData);
    console.error('Response Data:', error.response?.data);
    console.error('Request Config:', error.config);
    console.groupEnd();
  }

  // In production, you might want to send this to an error tracking service
  // e.g., Sentry, LogRocket, etc.
}
